import { useEffect, useState } from 'react';
import axios from 'axios';
import { isInCfCooldown, cfCooldownRemainingMs } from '../api/apiClient';

const API_URL = window.__BACKEND_URL__;

/**
 * formatPresence — Renders the friendly presence string the brief
 * mandates:
 *
 *   • online + heartbeat <30s     → "Active now"
 *   • offline, <60min              → "Active N minute(s) ago"
 *   • offline, today               → "Active today at HH:MM"
 *   • offline, yesterday           → "Active yesterday at HH:MM"
 *   • offline, this year           → "Last active 24 Apr at 14:35"
 *   • offline, older               → "Last active 24 Apr 2024 at 14:35"
 *
 * NOTE: presence respects privacy. When `visible: false` is returned
 * by the API, this helper returns null so the UI hides the row.
 */
export function formatPresence({ online, last_active_at, visible = true }) {
  if (!visible) return null;
  if (online) return 'Active now';
  if (!last_active_at) return null;

  const last = new Date(last_active_at);
  if (isNaN(last.getTime())) return null;
  const now = new Date();
  const diffMs = now - last;
  const diffMin = Math.floor(diffMs / 60000);

  // Treat <30s as "Active now" — the WS heartbeat updates every 30s so
  // this gracefully covers brief gaps between heartbeats.
  if (diffMs < 30 * 1000) return 'Active now';
  if (diffMin < 60) return `Active ${diffMin} minute${diffMin === 1 ? '' : 's'} ago`;

  const sameDay = last.toDateString() === now.toDateString();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = last.toDateString() === yesterday.toDateString();
  const time = last.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

  if (sameDay) return `Active today at ${time}`;
  if (isYesterday) return `Active yesterday at ${time}`;

  const sameYear = last.getFullYear() === now.getFullYear();
  const date = last.toLocaleDateString('en-GB', sameYear
    ? { day: 'numeric', month: 'short' }
    : { day: 'numeric', month: 'short', year: 'numeric' });
  return `Last active ${date} at ${time}`;
}

/**
 * usePresence — small hook that polls /api/users/{id}/presence every
 * 60 seconds and exposes `{ online, lastActiveAt, visible, label,
 * professionalStatus }`.
 *
 * Why polling not WS? The existing message WS multiplexes typing /
 * read receipts / new messages and adding presence frames there
 * widens the schema. A 60s poll keeps presence accurate enough for
 * the "Active now / Active 5m ago" use case with zero new infra.
 */
export default function usePresence(userId) {
  const [state, setState] = useState({ online: false, lastActiveAt: null, visible: true, label: null, professionalStatus: null, contactAvailability: null });

  useEffect(() => {
    // Skip entirely if there's no valid user id — prevents the
    // /presence endpoint from being called with `undefined` / empty
    // strings, which the backend rejects with a 400 and was the source
    // of the noisy 400-spam in the browser console.
    const idStr = typeof userId === 'string' ? userId : (userId ? String(userId) : '');
    if (!idStr || idStr.length < 6) {
      setState({ online: false, lastActiveAt: null, visible: false, label: null, professionalStatus: null, contactAvailability: null });
      return;
    }
    let alive = true;
    // Cache last 4xx (privacy/not-found/invalid id) so we don't hammer
    // the endpoint every 60s for users we already know are hidden /
    // not-present. Pollers must NOT keep poking the origin once we've
    // had a definitive "presence not available" answer.
    let suppressedUntilOnline = false;
    const fetchOnce = async () => {
      // Iter-rescue 2026-02 — skip the poll tick during a Cloudflare /
      // origin cooldown OR while a Messenger call / Record Video is
      // active. The interval will re-arm on its next natural tick.
      if (isInCfCooldown()) {
        // eslint-disable-next-line no-console
        console.info('[ccuk-network] request-skipped-cooldown', { url_safe: '/api/users/:id/presence', remaining_ms: cfCooldownRemainingMs() });
        return;
      }
      if (suppressedUntilOnline) {
        // eslint-disable-next-line no-console
        console.info('[ccuk-presence] presence-404-cached', { user_id_safe: idStr.slice(0, 4) + '…' });
        return;
      }
      try {
        const { data, status } = await axios.get(
          `${API_URL}/api/users/${encodeURIComponent(idStr)}/presence`,
          {
            withCredentials: true,
            // Treat any 4xx as "presence unknown" without axios logging
            // an Error in the console — privacy / not-found / invalid
            // are all the same UX outcome (hide the dot).
            validateStatus: (s) => s >= 200 && s < 500,
          },
        );
        if (!alive) return;
        // Non-2xx response → treat as "unknown / hidden" AND cache so
        // we stop hammering this endpoint until the user's online
        // status flips (next focus/online event triggers a fresh
        // fetchOnce by re-running the effect).
        if (status >= 400 || !data || typeof data !== 'object' || data.detail || data.error) {
          if (status === 404 || status === 403 || status === 400) {
            suppressedUntilOnline = true;
          }
          setState({ online: false, lastActiveAt: null, visible: false, label: null, professionalStatus: null, contactAvailability: null });
          return;
        }
        const next = {
          online: !!data.online,
          lastActiveAt: data.last_active_at || null,
          visible: data.visible !== false,
          // Manual professional status / contact availability — only
          // meaningful when presence is visible (the backend already
          // hides these for non-self / non-admin viewers when
          // show_presence is off, but we double-gate client-side too).
          professionalStatus: data.visible !== false ? (data.professional_status || null) : null,
          contactAvailability: data.visible !== false ? (data.contact_availability || null) : null,
        };
        next.label = formatPresence({
          online: next.online,
          last_active_at: next.lastActiveAt,
          visible: next.visible,
        });
        setState(next);
      } catch {
        if (alive) setState({ online: false, lastActiveAt: null, visible: false, label: null, professionalStatus: null, contactAvailability: null });
      }
    };
    fetchOnce();
    const id = setInterval(fetchOnce, 60_000);
    return () => { alive = false; clearInterval(id); };
  }, [userId]);

  return state;
}
